<template>
	<div v-if="getFinancialChartData" class="report_section_container">
		<div v-for="(item, idx) in currentData" :key="idx" class="section_finance">
			<h2 class="report_title">{{ getTitle(String(idx)) }}</h2>
			<div class="tab_unit_container">
				<ConnectedSquareTab
					v-if="String(idx) !== '배당'"
					:items="detailTabItems"
					:sort="String(idx)"
					@update:detail-tab="onChangePeriod"
				/>
				<span v-if="hasUnit(String(idx))">{{ `단위: ${hasUnit(String(idx))}` }}</span>
			</div>
			<FinanceChartItemJPN :chart-data="item" :sort="String(idx)" />
			<DescriptionBox :description="getDescription(String(idx))" />
		</div>
	</div>
</template>

<script setup lang="ts">
// core
import { computed, onMounted, Ref, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

// api
import { useGetFinancialChartApi_JPN } from '@/api/report';
// component
import ConnectedSquareTab from '@/components/atom/tabBar/ConnectedSquareTab.vue';
import DescriptionBox from '@/components/report/DescriptionBox.vue';
import FinanceChartItemJPN from '@/components/report/jpn/FinanceChartItemJPN.vue';
import {
	PBR과BPS,
	PER과EPS,
	ROE과ROIC,
	매출액영업이익,
	매출액영업이익증가율,
	배당,
	부채비율,
	영업이익률당기순이익률,
	유동비율당좌비율,
} from '@/constants/description.ts';
// constant
import { detailTabItems } from '@/constants/menu.ts';

// -----variable-----
const route = useRoute();
type TabStateType = {
	[key: string]: string;
};
const tabState: Ref<TabStateType> = ref({
	매출액영업이익: '연간',
	영업이익률당기순이익률: '연간',
	매출액영업이익증가율: '연간',
	유동비율당좌비율: '연간',
	부채비율: '연간',
	PER과EPS: '연간',
	PBR과BPS: '연간',
	ROE과ROIC: '연간',
	배당: '분기',
});
const currentData = ref();

// -----api-----
const { data: getFinancialChartData, refetch: refetchGetFinancialChart } = useGetFinancialChartApi_JPN(
	computed(() => {
		return {
			event_pk: String(route.params.eventPk),
		};
	})
);

// -----function-----
// 연간/분기 변경 시 실행 함수
const onChangePeriod = (index: string, item: string) => {
	tabState.value[item] = index;
	console.log('tabState.value[item]:', tabState.value[item]);
};
// 제목 추출 함수
const getTitle = (key: string) => {
	switch (key) {
		case '매출액영업이익':
			return '매출액&영업이익';
		case '영업이익률당기순이익률':
			return '영업이익률&당기순이익률';
		case '매출액영업이익증가율':
			return '매출액&영업이익 증가율';
		case '유동비율당좌비율':
			return '유동비율&당좌비율';
		case '부채비율':
			return '부채비율';
		case 'PER과EPS':
			return 'PER 과 EPS';
		case 'PBR과BPS':
			return 'PBR 과 BPS';
		case 'ROE과ROIC':
			return 'ROE 와 ROIC';
		case '배당':
			return '배당';
	}
};
// 설명 추출 함수
const getDescription = (key: string) => {
	switch (key) {
		case '매출액영업이익':
			return 매출액영업이익;
		case '영업이익률당기순이익률':
			return 영업이익률당기순이익률;
		case '매출액영업이익증가율':
			return 매출액영업이익증가율;
		case '유동비율당좌비율':
			return 유동비율당좌비율;
		case '부채비율':
			return 부채비율;
		case 'PER과EPS':
			return PER과EPS;
		case 'PBR과BPS':
			return PBR과BPS;
		case 'ROE과ROIC':
			return ROE과ROIC;
		case '배당':
			return 배당;
		default:
			return '';
	}
};
// 단위 노출 여부 확인 함수
const hasUnit = (key: string) => {
	switch (key) {
		case '매출액영업이익':
			return '백만엔(JPY)';
		case '영업이익률당기순이익률':
			return '%';
		case '매출액영업이익증가율':
			return '%';
		case '유동비율당좌비율':
			return '%';
		case '부채비율':
			return '%';
		case 'PER과EPS':
			return false;
		case 'PBR과BPS':
			return false;
		case 'ROE과ROIC':
			return '%';
		case '배당':
			return false;
		default:
			return false;
	}
};
// 데이터 가공 후 업데이트 하는 함수
const updateCurrentData = () => {
	if (getFinancialChartData.value) {
		currentData.value = {};
		if (getFinancialChartData.value[tabState.value.매출액영업이익]?.매출액_영업이익) {
			currentData.value.매출액영업이익 = getFinancialChartData.value[tabState.value.매출액영업이익].매출액_영업이익;
		}

		if (getFinancialChartData.value[tabState.value.영업이익률당기순이익률]?.영업이익률_당기순이익률) {
			currentData.value.영업이익률당기순이익률 =
				getFinancialChartData.value[tabState.value.영업이익률당기순이익률].영업이익률_당기순이익률;
		}

		if (getFinancialChartData.value[tabState.value.매출액영업이익증가율]?.매출액_영업이익_증가율) {
			currentData.value.매출액영업이익증가율 =
				getFinancialChartData.value[tabState.value.매출액영업이익증가율].매출액_영업이익_증가율;
		}

		if (getFinancialChartData.value[tabState.value.유동비율당좌비율]?.유동비율_당좌비율) {
			currentData.value.유동비율당좌비율 =
				getFinancialChartData.value[tabState.value.유동비율당좌비율].유동비율_당좌비율;
		}

		if (getFinancialChartData.value[tabState.value.부채비율]?.부채비율) {
			currentData.value.부채비율 = getFinancialChartData.value[tabState.value.부채비율].부채비율;
		}

		if (getFinancialChartData.value[tabState.value.PER과EPS]?.PER과_EPS) {
			currentData.value.PER과EPS = getFinancialChartData.value[tabState.value.PER과EPS].PER과_EPS;
		}

		if (getFinancialChartData.value[tabState.value.PBR과BPS]?.PBR과_BPS) {
			currentData.value.PBR과BPS = getFinancialChartData.value[tabState.value.PBR과BPS].PBR과_BPS;
		}

		if (getFinancialChartData.value[tabState.value.ROE과ROIC]?.ROE과_ROIC) {
			currentData.value.ROE과ROIC = getFinancialChartData.value[tabState.value.ROE과ROIC].ROE과_ROIC;
		}

		if (getFinancialChartData.value[tabState.value.배당]?.배당) {
			currentData.value.배당 = getFinancialChartData.value[tabState.value.배당].배당;
		}
	}
};

// -----composition-----
watch(
	() => tabState.value,
	() => {
		updateCurrentData();
	},
	{ immediate: true, deep: true }
);

onMounted(async () => {
	await refetchGetFinancialChart();
	updateCurrentData();
});
</script>

<style scoped lang="scss">
.report_section_container {
	display: flex;
	flex-direction: column;
	gap: 5.6rem;
	.section_finance {
		display: flex;
		flex-direction: column;
		gap: 2.4rem;
		.report_title {
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
		.tab_unit_container {
			display: flex;
			align-items: center;
			justify-content: space-between;
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);
			line-height: 15.51px;
			color: var(--color-base-light);
		}
	}
}
</style>
